"use client";

import { useState, useMemo, useEffect } from "react";
import { Loader2, Trash2, RefreshCw, CheckSquare, Square } from "lucide-react";
import {
  type EmptyTokenAccount,
  shortenAddress,
  FORGEPAD_FEE_PERCENT,
  TYPICAL_RENT_PER_ACCOUNT,
} from "@/lib/burn";

// Net amount per account after fee (used for display only)
const NET_RECLAIM_PER_ACCOUNT = TYPICAL_RENT_PER_ACCOUNT * (1 - FORGEPAD_FEE_PERCENT / 100);

interface EmptyAccountsSectionProps {
  emptyAccounts: EmptyTokenAccount[];
  onCloseAccounts: (accounts: EmptyTokenAccount[]) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  isClosing: boolean;
}

export function EmptyAccountsSection({
  emptyAccounts,
  onCloseAccounts,
  onRefresh,
  isRefreshing,
  isClosing,
}: EmptyAccountsSectionProps) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Track selected accounts by pubkey string
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Initialize all accounts as selected when list changes
  useEffect(() => {
    const allKeys = new Set(emptyAccounts.map(a => a.pubkey.toBase58()));
    setSelectedKeys(allKeys);
  }, [emptyAccounts]);

  if (emptyAccounts.length === 0) {
    return null;
  }

  // Get selected accounts
  const selectedAccounts = emptyAccounts.filter(a => selectedKeys.has(a.pubkey.toBase58()));

  // Calculate totals for selected accounts
  const totalRent = selectedAccounts.length * TYPICAL_RENT_PER_ACCOUNT;
  const platformFee = totalRent * FORGEPAD_FEE_PERCENT / 100;
  const netReclaim = totalRent - platformFee;

  const allSelected = selectedKeys.size === emptyAccounts.length;
  const noneSelected = selectedKeys.size === 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(emptyAccounts.map(a => a.pubkey.toBase58())));
    }
  };

  const toggleAccount = (pubkey: string) => {
    const newSelected = new Set(selectedKeys);
    if (newSelected.has(pubkey)) {
      newSelected.delete(pubkey);
    } else {
      newSelected.add(pubkey);
    }
    setSelectedKeys(newSelected);
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    onCloseAccounts(selectedAccounts);
    // Remove closed accounts from selection (they'll be removed from emptyAccounts after refresh)
    const closedKeys = new Set(selectedAccounts.map(a => a.pubkey.toBase58()));
    setSelectedKeys(prev => {
      const newSet = new Set(prev);
      closedKeys.forEach(k => newSet.delete(k));
      return newSet;
    });
  };

  return (
    <>
      <div className="p-4 bg-[#0d0d0d] border border-[#222] rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-[#f9cd4f]" />
            <h3 className="font-semibold text-white">Empty Token Accounts</h3>
            <span className="text-xs bg-[#f9cd4f]/20 text-[#f9cd4f] px-2 py-0.5 rounded-full">
              {emptyAccounts.length} found
            </span>
          </div>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-[#666] hover:text-[#888] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        <p className="text-sm text-[#888] mb-4">
          Select accounts to close and reclaim rent.
        </p>

        {/* Select All toggle */}
        <button
          onClick={toggleSelectAll}
          className="flex items-center gap-2 text-sm text-[#888] hover:text-white mb-3 transition-colors"
        >
          {allSelected ? (
            <CheckSquare className="w-4 h-4 text-[#f9cd4f]" />
          ) : (
            <Square className="w-4 h-4" />
          )}
          {allSelected ? "Deselect All" : "Select All"}
        </button>

        {/* Account list with checkboxes */}
        <div className="space-y-2 mb-4 max-h-[250px] overflow-y-auto">
          {emptyAccounts.map((account) => {
            const pubkeyStr = account.pubkey.toBase58();
            const isSelected = selectedKeys.has(pubkeyStr);

            return (
              <button
                key={pubkeyStr}
                onClick={() => toggleAccount(pubkeyStr)}
                className="flex items-center gap-3 p-2 w-full text-left bg-[#111] rounded-lg text-sm transition-colors hover:bg-[#1a1a1a] cursor-pointer"
              >
                {/* Checkbox */}
                {isSelected ? (
                  <CheckSquare className="w-4 h-4 text-[#f9cd4f] shrink-0" />
                ) : (
                  <Square className="w-4 h-4 text-[#555] shrink-0" />
                )}

                {/* Token info */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {account.logoURI ? (
                    <img
                      src={account.logoURI}
                      alt={account.symbol || "Token"}
                      className="w-6 h-6 rounded-full shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[#222] flex items-center justify-center text-xs text-[#666] shrink-0">
                      ?
                    </div>
                  )}
                  <span className="text-[#aaa] truncate">
                    {account.symbol || shortenAddress(account.mint.toBase58(), 4)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Close button */}
        <button
          onClick={() => setShowConfirmModal(true)}
          disabled={isClosing || noneSelected || selectedAccounts.length === 0}
          className="w-full py-3 bg-gradient-to-br from-[#f9cd4f] to-[#e6a800] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isClosing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Closing accounts...
            </>
          ) : noneSelected ? (
            <>
              <Square className="w-4 h-4" />
              Select accounts to close
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4" />
              Close {selectedAccounts.length} Selected — Reclaim ~{netReclaim.toFixed(4)} SOL
            </>
          )}
        </button>

        {/* Multi-transaction note when > 10 selected */}
        {selectedAccounts.length > 10 && !isClosing && (
          <p className="text-xs text-[#666] text-center mt-3">
            Selected {selectedAccounts.length} accounts — will be processed in {Math.ceil(selectedAccounts.length / 10)} transactions
          </p>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-5"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="bg-[#0a0a0a] rounded-2xl border border-[#333] p-6 max-w-[400px] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-center mb-4">
              <span className="mr-2">
                <Trash2 className="inline w-5 h-5" />
              </span>
              Close Empty Accounts
            </h2>

            <div className="bg-[#111] rounded-lg p-4 mb-4">
              <div className="text-center text-sm text-[#aaa] mb-2">
                Closing {selectedAccounts.length} selected account{selectedAccounts.length > 1 ? "s" : ""}
              </div>
              <div className="text-center text-[#f9cd4f] font-semibold text-lg">
                You will receive ~{netReclaim.toFixed(4)} SOL
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3.5 bg-[#222] border-none rounded-lg text-[#888] text-base font-semibold cursor-pointer hover:bg-[#2a2a2a] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3.5 bg-gradient-to-br from-[#f9cd4f] to-[#e6a800] border-none rounded-lg text-black text-base font-bold cursor-pointer hover:opacity-90 transition-opacity"
              >
                Close & Reclaim
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
